/**
 * Region responses.
 *
 * A page that opts in (see the `PartialPage` attribute) is answered with the document
 * title plus the page region only, instead of the whole document — the application
 * shell around the region is left out. Navigation then swaps that region inside the
 * document the user already has, so the shell is neither sent nor re-rendered.
 *
 * The swap reuses the primitives of a full page swap (DOM replacement plus Alpine's
 * tree lifecycle), which is what makes Livewire mount the incoming component and
 * destroy the outgoing one the same way it does after a body swap.
 */

export const REGION_ATTRIBUTE = 'data-livewire-partial'

/**
 * Name of the region carried by a response, or null for a regular document.
 */
export function partialRegion(html) {
    let match = html.match(new RegExp(`${REGION_ATTRIBUTE}="([^"]+)"`))

    return match ? match[1] : null
}

/**
 * Title carried by a response.
 */
function titleOf(html) {
    let match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i)

    return match ? match[1].trim() : null
}

/**
 * Script and stylesheet identities of a document, sorted so two documents can be
 * compared. Compared as pathname + query so an absolute `http://…` tag and a relative
 * `/…` tag of the same asset are recognised as the same one.
 */
function assetKeys(document) {
    return [
        ...Array.from(document.querySelectorAll('script[src]')).map(el => el.getAttribute('src')),
        ...Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(el => el.getAttribute('href')),
    ].filter(url => !! url).map(assetKey).sort()
}

/**
 * Identity of a single asset URL: pathname + query, without the origin.
 */
function assetKey(url) {
    try {
        let resolved = new URL(url, window.location.origin)

        return resolved.pathname + resolved.search
    } catch (error) {
        return url
    }
}

/**
 * Assets the response needs that this document hasn't loaded yet.
 *
 * A response can be swapped in as long as it needs nothing new. The shell injects its
 * own scripts at runtime (Livewire's among them), so a payload is normally a subset of
 * what is already on the page — an asset the page doesn't have (a stylesheet or script
 * from another entry point) can only be applied by a full visit.
 */
function assetsMissingFrom(document, incoming) {
    let loaded = assetKeys(document)

    return assetKeys(incoming).filter(key => ! loaded.includes(key))
}

/**
 * Swap the live region with the one carried by the response.
 *
 * Returns false when the response can't be swapped in — a different region, different
 * assets, or a shape we don't recognise. The caller then visits the destination as a
 * normal page, so a failed swap is never a dead end.
 */
export function swapRegionWithNewHtml(html, andThen) {
    let region = partialRegion(html)
    let live = document.querySelector(`[${REGION_ATTRIBUTE}="${region}"]`)

    if (! live) return false

    let incomingDocument = (new DOMParser()).parseFromString(html, 'text/html')
    let incoming = incomingDocument.querySelector(`[${REGION_ATTRIBUTE}="${region}"]`)

    if (! incoming) return false

    // Assets this page doesn't have can only be applied by a full visit.
    if (assetsMissingFrom(document, incomingDocument).length > 0) return false

    let old = live

    old.replaceWith(document.adoptNode(incoming))

    Alpine.destroyTree(old)
    Alpine.initTree(document.querySelector(`[${REGION_ATTRIBUTE}="${region}"]`))

    let title = titleOf(html)

    if (title !== null) document.title = title

    andThen(() => {})

    return true
}
